import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FullWidthContainer } from '#src/atoms/layout/FullWidthContainer/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, HeadlineS, LabelS } from '#src/atoms/typography/index.tsx';
import { Row } from '#src/components/seo/ui.tsx';
import { glossary } from '#src/lib/glossary.ts';

import type { Props as SquadPlayerProps } from './SquadPlayer/index.tsx';
import { SquadPlayer } from './SquadPlayer/index.tsx';
import { squadGridTemplateColumns } from './gridTemplateColumns.ts';

const StyledLabelS = styled(LabelS)`
  text-transform: uppercase;
`;

type Props<T extends string> = {
  displayName: string;
  stats: SquadPlayerProps<T>['stats'];
  statWidths?: SquadPlayerProps<T>['statWidths'];
  headers: Record<T, ReactNode>;
  groups: {
    label?: string;
    players: SquadPlayerProps<T>['player'][];
  }[];
};

export const Squad = <T extends string>({
  displayName,
  groups,
  headers,
  stats,
  statWidths,
}: Props<T>) => {
  return (
    <FullWidthContainer>
      <Vertical gap={1.5}>
        <HeadlineS as="h1">
          <FormattedMessage
            defaultMessage="{team} players"
            values={{ team: displayName }}
          />
        </HeadlineS>
        <Vertical gap={2}>
          <Row
            style={{
              gridTemplateColumns: squadGridTemplateColumns(stats, statWidths),
            }}
          >
            <StyledLabelS as="p" color="var(--c-nd-600)" bold>
              <FormattedMessage {...glossary.name} />
            </StyledLabelS>
            <span />
            {stats.map(stat => (
              <LabelS as="p" key={stat} color="var(--c-nd-600)" bold>
                {headers[stat]}
              </LabelS>
            ))}
          </Row>
          {groups.map(group => (
            <Vertical gap={1.5} key={group.label}>
              {!!group.label && (
                <BodyM as="h2" color="var(--c-nd-600)">
                  {group.label}
                </BodyM>
              )}
              <Vertical gap={3}>
                {group.players.map(player => (
                  <SquadPlayer
                    key={player.slug}
                    player={player}
                    stats={stats}
                    statWidths={statWidths}
                  />
                ))}
              </Vertical>
            </Vertical>
          ))}
        </Vertical>
      </Vertical>
    </FullWidthContainer>
  );
};
